import { useEffect, useState, type ReactNode } from 'react'
import * as Popover from '@radix-ui/react-popover'
import type { Member, Tag, UserRef } from '../lib/types'
import { PRIORITIES, PRIORITY_LABELS, priorityMark } from '../lib/format'
import { DUE_LABELS, EMPTY_FILTERS, activeFilterCount, type Filters } from '../lib/filters'
import { Avatar, Button, TagChip, cx } from './ui'

/**
 * Board filters.
 *
 * Every control writes straight to the URL, so a filtered board is a link you
 * can paste to somebody, the back button undoes a filter, and Export for AI
 * can send the identical query to the server — the thing you exported is
 * literally the thing you were looking at.
 */
export function FilterBar({
  filters, onChange, members, tags, currentUser, trailing,
}: {
  filters: Filters
  onChange: (next: Filters) => void
  members: Member[]
  tags: Tag[]
  currentUser: UserRef
  trailing?: ReactNode
}) {
  const [text, setText] = useState(filters.q)

  // The board refetches on every filter change, so the text box is debounced
  // rather than firing a request per keystroke.
  useEffect(() => setText(filters.q), [filters.q])
  useEffect(() => {
    if (text === filters.q) return
    const timer = window.setTimeout(() => onChange({ ...filters, q: text }), 250)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text])

  const count = activeFilterCount(filters)
  const usedTags = tags.filter((t) => (t.usageCount ?? 0) > 0 || filters.tags.includes(t.id))

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--line)] px-4 py-2">
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Filter by text…"
        aria-label="Filter tasks by text"
        className={cx(
          'h-7 w-[168px] rounded-md border border-[var(--line)] bg-[var(--surface)] px-2',
          'text-[12.5px] outline-none transition-colors duration-[140ms]',
          'placeholder:text-[var(--ink-3)] hover:border-[var(--line-strong)]',
          'focus:border-[var(--accent)]',
        )}
      />

      <AssigneeFilter
        filters={filters}
        onChange={onChange}
        members={members}
        currentUser={currentUser}
      />

      <FilterMenu label="Tag" active={filters.tags.length}>
        {usedTags.length === 0 && (
          <p className="px-2 py-3 text-center text-[12px] text-[var(--ink-3)]">
            No tags in use yet.
          </p>
        )}
        {usedTags.map((tag) => (
          <MenuItem
            key={tag.id}
            checked={filters.tags.includes(tag.id)}
            onSelect={() => onChange({ ...filters, tags: toggle(filters.tags, tag.id) })}
          >
            <span
              aria-hidden
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: tag.color }}
            />
            {tag.name}
            {tag.usageCount ? (
              <span className="task-key ml-auto text-[10.5px] text-[var(--ink-3)]">
                {tag.usageCount}
              </span>
            ) : null}
          </MenuItem>
        ))}

        {filters.tags.length > 1 && (
          <div className="mt-1 border-t border-[var(--line)] px-2 pt-2">
            <label className="flex cursor-pointer items-center gap-2 text-[12px] text-[var(--ink-2)]">
              <input
                type="checkbox"
                checked={filters.tagsMatchAll}
                onChange={(e) => onChange({ ...filters, tagsMatchAll: e.target.checked })}
                className="accent-[var(--accent)]"
              />
              Match all selected tags
            </label>
          </div>
        )}
      </FilterMenu>

      <FilterMenu label="Priority" active={filters.priorities.length}>
        {PRIORITIES.map((p) => (
          <MenuItem
            key={p}
            checked={filters.priorities.includes(p)}
            onSelect={() => onChange({ ...filters, priorities: toggle(filters.priorities, p) })}
          >
            <span className="task-key w-4 font-bold text-[var(--danger)]">{priorityMark(p)}</span>
            {PRIORITY_LABELS[p]}
          </MenuItem>
        ))}
      </FilterMenu>

      <FilterMenu label="Due" active={filters.due ? 1 : 0}>
        {(Object.keys(DUE_LABELS) as Array<keyof typeof DUE_LABELS>).map((key) => (
          <MenuItem
            key={key}
            checked={filters.due === key}
            onSelect={() => onChange({ ...filters, due: filters.due === key ? '' : key })}
          >
            {DUE_LABELS[key]}
          </MenuItem>
        ))}
      </FilterMenu>

      {count > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
          className="text-[var(--ink-3)]"
        >
          Clear {count}
        </Button>
      )}

      <div className="ml-auto flex items-center gap-1.5">{trailing}</div>
    </div>
  )
}

/**
 * Who a task is for, answered without opening anything.
 *
 * "Mine" is the question people ask most, so it is a labelled button rather
 * than one row of a menu. The rest of the team is a row of faces you click:
 * recognising a colleague by their avatar is faster than reading a list, and
 * the whole control is one tap deep. Only the overflow past six people needs
 * a menu, and anyone already filtered on stays out on the bar so a filter can
 * never hide behind a “+4”.
 */
const INLINE_FACES = 6

function AssigneeFilter({
  filters, onChange, members, currentUser,
}: {
  filters: Filters
  onChange: (next: Filters) => void
  members: Member[]
  currentUser: UserRef
}) {
  const mine = filters.assignees.includes(currentUser.id)

  function toggleAssignee(id: string) {
    onChange({
      ...filters,
      assignees: filters.assignees.includes(id)
        ? filters.assignees.filter((v) => v !== id)
        : [...filters.assignees, id],
    })
  }

  const others = members.filter((m) => m.user.id !== currentUser.id)
  // A stable slice, so faces do not reshuffle under the cursor as you click.
  const head = others.slice(0, INLINE_FACES)
  const rest = others.slice(INLINE_FACES)
  const inline = [...head, ...rest.filter((m) => filters.assignees.includes(m.user.id))]
  const overflow = rest.filter((m) => !filters.assignees.includes(m.user.id))

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => toggleAssignee(currentUser.id)}
        aria-pressed={mine}
        className={cx(
          'flex h-7 items-center gap-1.5 rounded-md border pl-1 pr-2 text-[12.5px]',
          'transition-colors duration-[140ms]',
          mine
            ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
            : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)] hover:border-[var(--line-strong)] hover:text-[var(--ink)]',
        )}
      >
        <Avatar user={currentUser} size={20} />
        Mine
      </button>

      {(inline.length > 0 || overflow.length > 0) && (
        <div className="flex items-center gap-1">
          {inline.map((m) => (
            <FaceToggle
              key={m.user.id}
              on={filters.assignees.includes(m.user.id)}
              label={m.user.name}
              onClick={() => toggleAssignee(m.user.id)}
            >
              <Avatar user={m.user} size={22} />
            </FaceToggle>
          ))}

          {overflow.length > 0 && (
            <FilterMenu label={`+${overflow.length}`} active={0}>
              {overflow.map((m) => (
                <MenuItem
                  key={m.user.id}
                  checked={false}
                  onSelect={() => toggleAssignee(m.user.id)}
                >
                  <Avatar user={m.user} size={18} />
                  {m.user.name}
                </MenuItem>
              ))}
            </FilterMenu>
          )}
        </div>
      )}

      {/* An empty dashed avatar is the established symbol for "nobody", so it
          needs no label — but it does need room, or it reads as a divider
          between the faces and the menus rather than a control of its own. */}
      <FaceToggle
        on={filters.unassigned}
        label="Nobody yet"
        onClick={() => onChange({ ...filters, unassigned: !filters.unassigned })}
        className="ml-0.5"
        dim={false}
      >
        <span
          aria-hidden
          className={cx(
            'h-[22px] w-[22px] rounded-full border border-dashed',
            filters.unassigned ? 'border-[var(--accent)]' : 'border-[var(--line-strong)]',
          )}
        />
      </FaceToggle>
    </div>
  )
}

/**
 * A round toggle: the ring says it is on, so the face itself stays legible.
 *
 * Faces rest dimmed, which is what makes a selected one obvious in a row of
 * eight. An outline that is already drawn in hairlines has nothing to dim, so
 * it opts out.
 */
function FaceToggle({
  on, label, onClick, children, className, dim = true,
}: {
  on: boolean
  label: string
  onClick: () => void
  children: ReactNode
  className?: string
  dim?: boolean
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={on}
      className={cx(
        'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full',
        'transition-[box-shadow,opacity] duration-[140ms]',
        on
          ? 'shadow-[0_0_0_2px_var(--accent)]'
          : cx(dim && 'opacity-55', 'hover:opacity-100 hover:shadow-[0_0_0_2px_var(--line-strong)]'),
        className,
      )}
    >
      {children}
    </button>
  )
}

function FilterMenu({
  label, active, children,
}: {
  label: string
  active: number
  children: ReactNode
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          className={cx(
            'flex h-7 items-center gap-1 rounded-md border px-2 text-[12.5px]',
            'transition-colors duration-[140ms]',
            active > 0
              ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
              : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink-2)] hover:border-[var(--line-strong)] hover:text-[var(--ink)]',
          )}
        >
          {label}
          {active > 0 && <span className="task-key text-[11px]">{active}</span>}
          <ChevronGlyph />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={5}
          className={cx(
            'fade-in z-50 max-h-[320px] w-[224px] overflow-y-auto rounded-lg border border-[var(--line)]',
            'bg-[var(--surface)] p-1 shadow-xl shadow-black/10 dark:shadow-black/50',
          )}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function MenuItem({
  checked, onSelect, children,
}: {
  checked: boolean
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <button
      onClick={onSelect}
      role="menuitemcheckbox"
      aria-checked={checked}
      className={cx(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px]',
        'transition-colors duration-[110ms] hover:bg-[var(--sunken)]',
        checked ? 'text-[var(--ink)]' : 'text-[var(--ink-2)]',
      )}
    >
      <span
        aria-hidden
        className={cx(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border',
          checked
            ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
            : 'border-[var(--line-strong)]',
        )}
      >
        {checked && (
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m2.5 6.2 2.3 2.3 4.7-5" />
          </svg>
        )}
      </span>
      {children}
    </button>
  )
}

/** The chips under the bar, so what is filtered stays visible while you work. */
export function ActiveFilterChips({
  filters, onChange, members, tags,
}: {
  filters: Filters
  onChange: (next: Filters) => void
  members: Member[]
  tags: Tag[]
}) {
  if (activeFilterCount(filters) === 0) return null

  const selectedTags = tags.filter((t) => filters.tags.includes(t.id))
  const selectedPeople = members.filter((m) => filters.assignees.includes(m.user.id))

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--line)] px-4 py-1.5">
      {selectedPeople.map((m) => (
        <Chip
          key={m.user.id}
          onRemove={() =>
            onChange({ ...filters, assignees: filters.assignees.filter((id) => id !== m.user.id) })
          }
        >
          <Avatar user={m.user} size={14} />
          {m.user.name}
        </Chip>
      ))}
      {filters.unassigned && (
        <Chip onRemove={() => onChange({ ...filters, unassigned: false })}>Unassigned</Chip>
      )}
      {selectedTags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onRemove={() =>
            onChange({ ...filters, tags: filters.tags.filter((id) => id !== tag.id) })
          }
        />
      ))}
      {filters.priorities.map((p) => (
        <Chip
          key={p}
          onRemove={() =>
            onChange({ ...filters, priorities: filters.priorities.filter((v) => v !== p) })
          }
        >
          {PRIORITY_LABELS[p]}
        </Chip>
      ))}
      {filters.due && (
        <Chip onRemove={() => onChange({ ...filters, due: '' })}>{DUE_LABELS[filters.due]}</Chip>
      )}
      {filters.q.trim() && (
        <Chip onRemove={() => onChange({ ...filters, q: '' })}>“{filters.q.trim()}”</Chip>
      )}
      {filters.archived && (
        <Chip onRemove={() => onChange({ ...filters, archived: false })}>Archived only</Chip>
      )}
    </div>
  )
}

function Chip({ children, onRemove }: { children: ReactNode; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-[var(--line)] bg-[var(--sunken)] px-1.5 py-px text-[11px] text-[var(--ink-2)]">
      {children}
      <button
        onClick={onRemove}
        aria-label="Remove filter"
        className="ml-0.5 text-[var(--ink-3)] hover:text-[var(--ink)]"
      >
        ×
      </button>
    </span>
  )
}

function ChevronGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m4.5 6.5 3.5 3.5 3.5-3.5" />
    </svg>
  )
}
